import { Pipe, PipeTransform } from '@angular/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { SmartLabelOptions } from '../interfaces/smartlabel-options.interface';
import { SmartLabelService } from '../services/smartlabel.service';
import { TranslationService } from '../services/translation.service';

/**
 * Resolves a smartlabel `key` into display text, with optional placeholder substitution
 * and fallback handling. Placeholders can be replaced with provided values, which are
 * translated by default unless explicitly disabled.
 *
 * Usage examples (English):
 *   - {{ 'key' | smartlabel | async }}
 *       → "This is a smartlabel."
 *
 *   - {{ 'keyWithToken' | smartlabel | async }}
 *       → "This is a smartlabel with a [token]."
 *
 *   - {{ 'keyWithToken' | smartlabel:{ replacements: { token: 'replacement' } } | async }}
 *       → "This is a smartlabel with a replacement."
 *
 *   - {{ 'keyWithToken' | smartlabel:{ replacements: { token: 'replacement' }, translateReplacements: false } | async }}
 *       → "This is a smartlabel with a replacement." (without translating "replacement")
 *
 * Usage examples (English → Spanish):
 *   - {{ 'key' | smartlabel | async }}
 *       → "Esta es una etiqueta inteligente."
 *
 *   - {{ 'keyWithToken' | smartlabel:{ replacements: { token: 'replacement' } } | async }}
 *       → "Esta es una etiqueta inteligente con un reemplazo."
 *
 *   - {{ 'keyWithToken' | smartlabel:{ replacements: { token: 'replacement' }, translateReplacements: false } | async }}
 *       → "Esta es una etiqueta inteligente con un replacement."
 */
@Pipe({
    standalone: true,
    name: 'smartlabel',
})
export class SmartLabelPipe implements PipeTransform {
    constructor(
        /** TODO: This does not do what The Registry does. */
        private readonly smartLabelService: SmartLabelService,
        /** TODO: This does not do what The Registry does. */
        private readonly translationService: TranslationService,
    ) { }

    /**
     * Transforms a smartlabel key into display text, optionally substituting placeholders with replacements.
     * @param key The smartlabel key to be transformed.
     * @param options Options to customize the transformation behavior.
     * @returns An Observable that emits the transformed label text.
     */
    transform(key?: string | null, options?: SmartLabelOptions | null): Observable<string> {
        // If there is no supplied key, return an observable of an empty string
        if (!key) {
            return of('');
        }

        // Normalize options and replacements for consistent processing with default values
        const normalizedOptions: SmartLabelOptions = this.normalizeOptions(options);
        const normalizedReplacements: Record<string, string> = this.normalizeReplacements(options?.replacements);

        // If there are no replacements or translation of replacements is disabled, get the label text directly
        if (Object.keys(normalizedReplacements).length === 0 || !normalizedOptions.translateReplacements) {
            /** TODO: This does not do what The Registry does. */
            return this.smartLabelService
                .getLabelText(key, { replacements: normalizedReplacements })
                .pipe(
                    switchMap((labelText: string | undefined) =>
                        this.withFallback$(labelText, key, normalizedOptions, normalizedReplacements),
                    ),
                );
        }

        // Create an observable for each replacement to get its translated text
        const translatedReplacementsByKey$: Record<string, Observable<string | undefined>> = Object.keys(
            normalizedReplacements,
        ).reduce(
            (results: Record<string, Observable<string | undefined>>, key: string) => {
                /** TODO: This does not do what The Registry does. */
                results[key] = this.translationService.getText(normalizedReplacements[key]);
                return results;
            },
            {} as Record<string, Observable<string | undefined>>,
        );

        // Combine the latest values from all replacement observables and get the final label text
        // Note: Use `combineLatest` instead of `forkJoin` to handle dynamic replacement keys
        //    - Since the translation service is "hot", `forkJoin` will never complete
        //    - `combineLatest` will emit whenever any replacement observable emits a new value without the need of a manual refresh
        return combineLatest(translatedReplacementsByKey$).pipe(
            switchMap((translatedReplacements: Record<string, string | undefined>) =>
                /** TODO: This does not do what The Registry does. */
                this.smartLabelService
                    .getLabelText(key, { replacements: translatedReplacements })
                    .pipe(map((labelText: string | undefined) => ({ labelText, translatedReplacements }))),
            ),
            switchMap(({ labelText, translatedReplacements }) =>
                this.withFallback$(labelText, key, normalizedOptions, translatedReplacements),
            ),
        );
    }

    /**
     * Normalizes the options object by setting default values for missing properties.
     * @param options The options object to be normalized.
     * @returns A normalized options object with default values applied.
     */
    private normalizeOptions(options?: SmartLabelOptions | null): SmartLabelOptions {
        // Determine the base translation setting (default: true)
        const baseTranslate: boolean = options?.translate ?? true;

        // Return a new options object with defaults applied
        return {
            translate: baseTranslate,
            replacements: options?.replacements ?? null,
            translateReplacements: options?.translateReplacements ?? baseTranslate,
            fallback: options?.fallback ?? '',
            translateFallback: options?.translateFallback ?? baseTranslate,
        };
    }

    /**
     * Normalizes the replacements object by converting all values to strings.
     * @param replacements An object containing placeholder-replacement pairs.
     * @returns A normalized object with string values.
     */
    private readonly normalizeReplacements = (
        replacements?: Record<string, unknown> | null,
    ): Record<string, string> => {
        // If there are no replacements, return an empty object
        if (!replacements) {
            return {};
        }

        // Convert all replacement values to strings, ignoring null or undefined values
        const normalizedReplacements: Record<string, string> = {};
        for (const [key, value] of Object.entries(replacements)) {
            // Convert the value to a string
            const stringValue: string | null = this.stringifyValue(value);

            // If the string value is not null, add it to the normalized replacements (trimmed)
            if (stringValue !== null) {
                normalizedReplacements[key] = stringValue.trim();
            }
        }

        // Return the normalized replacements
        return normalizedReplacements;
    };

    /**
     * Converts various types of values to their string representation.
     * @param value The value to be converted.
     * @returns The string representation of the value, or null if it cannot be converted.
     */
    private readonly stringifyValue = (value: unknown): string | null => {
        if (value === null || value === undefined) {
            return null;
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        if (value instanceof Date) {
            return value.toLocaleDateString();
        }
        return null;
    };

    /**
     * Handles fallback logic for label text when the main text is missing or empty.
     * @param text The main label text to be checked.
     * @param _key The smartlabel key (for logging/debugging purposes).
     * @param options The options object containing fallback settings.
     * @param replacements An object containing placeholder-replacement pairs.
     * @returns An Observable that emits the final label text after applying fallback and replacements.
     */
    private readonly withFallback$ = (
        text: string | undefined,
        _key: string,
        options: SmartLabelOptions,
        replacements?: Record<string, string | undefined>,
    ): Observable<string> => {
        // If the main text is present and non-empty, return it as an observable
        if (text && text.trim().length > 0) {
            return of(text.trim());
        }

        // If there is no fallback, return an observable of an empty string
        if (!options.fallback || options.fallback.trim().length === 0) {
            return of('');
        }

        // If translation of the fallback is disabled, apply replacements directly to the fallback text
        if (!options.translateFallback) {
            return of(this.applyReplacements(options.fallback, replacements).trim());
        }

        // Otherwise, translate the fallback text and then apply replacements
        return (
            /** TODO: This does not do what The Registry does. */
            this.translationService
                .getText(options.fallback.trim())
                .pipe(
                    map((translatedText: string | undefined) =>
                        this.applyReplacements((translatedText ?? '').trim(), replacements).trim(),
                    ),
                )
        );
    };

    /**
     * Applies replacements to the given text by substituting placeholders with their corresponding values.
     * @param text The text containing placeholders to be replaced.
     * @param replacements An object containing placeholder-replacement pairs.
     * @returns The text with replacements applied.
     */
    private applyReplacements(text: string, replacements?: Record<string, string | undefined>): string {
        // If there are no replacements, return the original text
        if (!replacements || Object.keys(replacements).length === 0) {
            return text;
        }

        // Escape special characters in the replacement keys for use in a regular expression
        const escapeRegExp = (token: string): string => token.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

        // Replace each placeholder in the text with its corresponding replacement value
        let result: string = text;
        for (const [key, value] of Object.entries(replacements)) {
            // Skip null or undefined replacement values
            if (value === null || value === undefined) {
                continue;
            }

            // Create a global regex pattern to match the placeholder (e.g., [key])
            const pattern = new RegExp(String.raw`\[${escapeRegExp(key)}\]`, 'g');

            // Replace all occurrences of the placeholder with the corresponding value (trimmed)
            result = result.replace(pattern, value.trim());
        }

        // Return the final text with replacements applied
        return result;
    }
}
